// frontend/src/components/wizard/StepReview/FilterConfigPanel.jsx
/**
 * FilterConfigPanel — ¿Qué datos quieres migrar?
 *
 * UX simplificado: el usuario elige entre 3 opciones claras.
 * Sin jerga técnica. Compatible con todos los motores BD.
 * Para archivos planos (CSV, Excel, DBF, GS): panel deshabilitado con aviso.
 *
 * Modes internos:
 *   auto   → Desde la última ejecución (watermark automático)
 *   manual → El usuario elige el rango (fecha o valor numérico)
 *   cutover→ Solo en contexto de proyecto/fase (manejado por el orquestador)
 *
 * Input adaptativo:
 *   - Columnas tipo fecha (date, datetime, timestamp, bigint con sufijo *At)
 *     → input datetime-local (convierte a/desde Unix ms)
 *   - Columnas tipo numérico (int, bigint sin sufijo *At, decimal, float)
 *     → input number (valor directo)
 *   - Otros tipos → input text
 */
import React, { useState, useMemo } from 'react';
import { Filter, ChevronDown, ChevronRight, Info, Clock, Calendar } from 'lucide-react';
import { useLanguageStore } from '../../../store/useLanguageStore';

// ── Motores que soportan filtro delta ─────────────────────────────────
const FILTER_SUPPORTED_ENGINES = new Set([
  'mysql', 'postgres', 'postgresql', 'oracle', 'mssql', 'sqlserver',
  'mongodb', 'bigquery',
]);

// ── Detección de tipo de columna ──────────────────────────────────────
const DATE_TYPES = new Set([
  'date', 'datetime', 'datetime2', 'timestamp', 'timestamptz',
  'timestamp without time zone', 'timestamp with time zone',
  'smalldatetime',
]);

const NUMERIC_TYPES = new Set([
  'int', 'integer', 'bigint', 'smallint', 'tinyint', 'mediumint',
  'decimal', 'numeric', 'float', 'double', 'real', 'number',
]);

// Sufijos que indican "campo temporal" incluso si el tipo es bigint/int
const DATE_SUFFIXES = ['at', 'date', 'time', 'timestamp', 'fecha'];

/**
 * Determina el tipo de input a usar para una columna.
 * @param {string} colName  - Nombre de la columna
 * @param {string} colType  - Tipo SQL de la columna (puede ser null)
 * @returns {'date' | 'number' | 'text'}
 */
const detectInputType = (colName, colType) => {
  const typeLower = (colType || '').toLowerCase().split('(')[0].trim();
  const nameLower = (colName || '').toLowerCase();

  // 1. Tipo SQL explícitamente fecha → date
  if (DATE_TYPES.has(typeLower)) return 'date';

  // 2. bigint/int + nombre con sufijo temporal → date (ej: updatedAt, createdAt)
  if (NUMERIC_TYPES.has(typeLower)) {
    const hasDateSuffix = DATE_SUFFIXES.some(s =>
      nameLower.endsWith(s.toLowerCase())
    );
    if (hasDateSuffix) return 'date';
    return 'number';
  }

  // 3. Sin tipo pero nombre con sufijo temporal → date
  if (!typeLower) {
    const hasDateSuffix = DATE_SUFFIXES.some(s =>
      nameLower.endsWith(s.toLowerCase())
    );
    if (hasDateSuffix) return 'date';
  }

  return 'text';
};

// ── Helpers: conversión ms Unix ↔ datetime-local ──────────────────────
const msToDateInput = (ms) => {
  if (!ms) return '';
  const d = new Date(ms);
  return d.toISOString().slice(0, 16);
};

const dateInputToMs = (str) => {
  if (!str) return null;
  return new Date(str).getTime();
};

// ── Componente principal ───────────────────────────────────────────────
export const FilterConfigPanel = ({
  filterConfig,
  onChange,
  sourceEngine,
  sourceColumns = [],
}) => {
  const { t } = useLanguageStore();
  const [open, setOpen] = useState(false);

  const supported = FILTER_SUPPORTED_ENGINES.has((sourceEngine || '').toLowerCase());
  const enabled = filterConfig?.enabled ?? false;
  const mode = filterConfig?.mode ?? 'auto';
  const field = filterConfig?.field ?? '';
  const fromValue = filterConfig?.fromValue ?? null;
  const toValue = filterConfig?.toValue ?? null;

  const suggestedFrom = filterConfig?.watermarkValue || null;
  const suggestedLabel = suggestedFrom ? new Date(suggestedFrom).toLocaleString() : null;

  const update = (patch) => onChange({ ...filterConfig, ...patch });

  // ── Normalizar sourceColumns: soporta tanto [string] como [{name, type}]
  const normalizedColumns = useMemo(() =>
    sourceColumns.map(col =>
      typeof col === 'string' ? { name: col, type: null } : col
    ),
    [sourceColumns]
  );

  // ── Detectar tipo de input según la columna seleccionada ────────────
  const selectedColMeta = useMemo(() => {
    if (!field) return null;
    // En N:M las columnas vienen como "tabla.col" — buscar con y sin prefijo
    const cleanField = field.includes('.') ? field.split('.').pop() : field;
    return normalizedColumns.find(c => {
      const cleanName = c.name.includes('.') ? c.name.split('.').pop() : c.name;
      return c.name === field || cleanName === cleanField;
    }) || null;
  }, [field, normalizedColumns]);

  const inputType = useMemo(() =>
    detectInputType(field, selectedColMeta?.type),
    [field, selectedColMeta]
  );

  // ── Resumen colapsado ───────────────────────────────────────────────
  const summary = useMemo(() => {
    if (!enabled) return t('filter.status.all_data') || 'Todos los datos';
    if (mode === 'auto') return t('filter.status.auto') || 'Solo cambios recientes (automático)';
    if (mode === 'manual') return t('filter.status.manual') || 'Rango manual';
    return t('filter.status.enabled') || 'Filtro activo';
  }, [enabled, mode, t]);

  // ── Render del input Desde/Hasta según tipo ─────────────────────────
  const renderValueInput = (label, value, onChangeValue, required = false, hint = null) => {
    const commonClass = "w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400";

    return (
      <div>
        <label className="block text-xs font-bold text-slate-600 mb-1">
          {label} {required && '*'}
          {!required && (
            <span className="font-normal text-slate-400 ml-1">
              ({t('filter.to_optional') || 'opcional'})
            </span>
          )}
        </label>
        {required && suggestedLabel && (
          <p className="text-[10px] text-indigo-500 mb-1 flex items-center gap-1">
            <Info className="w-3 h-3" />
            {t('filter.suggested') || 'Sugerido:'} {suggestedLabel}
          </p>
        )}

        {inputType === 'date' ? (
          <input
            type="datetime-local"
            className={commonClass}
            value={msToDateInput(value)}
            onChange={e => onChangeValue(dateInputToMs(e.target.value))}
          />
        ) : inputType === 'number' ? (
          <input
            type="number"
            className={commonClass}
            placeholder={t('filter.number_placeholder') || 'Ej: 100'}
            value={value ?? ''}
            onChange={e => {
              const v = e.target.value;
              onChangeValue(v === '' ? null : Number(v));
            }}
          />
        ) : (
          <input
            type="text"
            className={commonClass}
            placeholder={t('filter.text_placeholder') || 'Valor de referencia'}
            value={value ?? ''}
            onChange={e => {
              const v = e.target.value;
              onChangeValue(v === '' ? null : v);
            }}
          />
        )}

        {hint && (
          <p className="text-[10px] text-slate-400 mt-1">{hint}</p>
        )}
      </div>
    );
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">

      {/* ── Cabecera colapsable ──────────────────────────────────────── */}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0
            ${enabled ? 'bg-indigo-50 border border-indigo-100' : 'bg-slate-50 border border-slate-200'}`}>
            <Filter className={`w-4 h-4 ${enabled ? 'text-indigo-600' : 'text-slate-400'}`} />
          </div>
          <div>
            <p className="text-sm font-bold text-slate-800">
              {t('filter.title') || '¿Qué datos migrar?'}
            </p>
            <p className="text-xs text-slate-500 mt-0.5">{summary}</p>
          </div>
        </div>
        {open
          ? <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />
          : <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />}
      </button>

      {/* ── Panel expandido ──────────────────────────────────────────── */}
      {open && (
        <div className="border-t border-slate-100 px-5 py-5 space-y-5 bg-slate-50">

          {/* Aviso si el motor no soporta filtro */}
          {!supported ? (
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-700">
              <Info className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{t('filter.not_supported') || 'El filtro delta no está disponible para archivos planos (CSV, Excel, DBF, Google Sheets). Se migrará el archivo completo.'}</span>
            </div>
          ) : (
            <>
              {/* ── Opción 1: Todos los datos (sin filtro) ──────────── */}
              <label className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition
                ${!enabled
                  ? 'border-indigo-400 bg-indigo-50'
                  : 'border-slate-200 bg-white hover:border-slate-300'}`}>
                <input
                  type="radio"
                  name="filter_mode"
                  checked={!enabled}
                  onChange={() => update({ enabled: false })}
                  className="mt-0.5 w-4 h-4 text-indigo-600 border-slate-300 focus:ring-indigo-400"
                />
                <div>
                  <p className="text-sm font-bold text-slate-800">
                    {t('filter.option.all') || 'Todos los datos'}
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {t('filter.option.all_desc') || 'Migra la tabla completa en cada ejecución'}
                  </p>
                </div>
              </label>

              {/* ── Opción 2: Solo cambios recientes (auto) ─────────── */}
              <label className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition
                ${enabled && mode === 'auto'
                  ? 'border-indigo-400 bg-indigo-50'
                  : 'border-slate-200 bg-white hover:border-slate-300'}`}>
                <input
                  type="radio"
                  name="filter_mode"
                  checked={enabled && mode === 'auto'}
                  onChange={() => update({ enabled: true, mode: 'auto', fromValue: null })}
                  className="mt-0.5 w-4 h-4 text-indigo-600 border-slate-300 focus:ring-indigo-400"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-slate-800 flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5 text-indigo-500" />
                    {t('filter.option.auto') || 'Solo cambios recientes'}
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {t('filter.option.auto_desc') || 'DatiaFlow recuerda la última ejecución y solo trae lo nuevo'}
                  </p>
                  {suggestedLabel && (
                    <p className="text-[10px] text-indigo-600 mt-1.5 flex items-center gap-1">
                      <Info className="w-3 h-3" />
                      {t('filter.option.auto_hint') || 'Próxima ejecución: desde'} {suggestedLabel}
                    </p>
                  )}
                  {!suggestedLabel && (
                    <p className="text-[10px] text-slate-400 mt-1.5">
                      {t('filter.option.auto_first') || 'Primera ejecución: migra todo. A partir de la segunda, solo los cambios.'}
                    </p>
                  )}
                </div>
              </label>

              {/* ── Opción 3: Rango manual ──────────────────────────── */}
              <label className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition
                ${enabled && mode === 'manual'
                  ? 'border-indigo-400 bg-indigo-50'
                  : 'border-slate-200 bg-white hover:border-slate-300'}`}>
                <input
                  type="radio"
                  name="filter_mode"
                  checked={enabled && mode === 'manual'}
                  onChange={() => update({ enabled: true, mode: 'manual' })}
                  className="mt-0.5 w-4 h-4 text-indigo-600 border-slate-300 focus:ring-indigo-400"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-slate-800 flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5 text-indigo-500" />
                    {t('filter.option.manual') || 'Rango manual'}
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {t('filter.option.manual_desc') || 'Tú defines desde qué valor migrar. Útil para pruebas o correcciones.'}
                  </p>
                </div>
              </label>

              {/* ── Configuración común (campo + valores) — solo si activo ── */}
              {enabled && (
                <div className="bg-white rounded-xl border border-slate-200 px-4 py-4 space-y-4">

                  {/* Campo de referencia */}
                  <div>
                    <label className="block text-xs font-bold text-slate-600 mb-1">
                      {t('filter.field_label') || 'Campo de referencia'}
                      <span className="ml-1 font-normal text-slate-400">
                        ({t('filter.field_hint') || 'columna con fecha o ID en el origen'})
                      </span>
                    </label>
                    {normalizedColumns.length > 0 ? (
                      <select
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-white"
                        value={field}
                        onChange={e => update({ field: e.target.value })}
                      >
                        <option value="">
                          — {t('filter.field_select') || 'Seleccionar columna'} —
                        </option>
                        {normalizedColumns.map(col => (
                          <option key={col.name} value={col.name}>
                            {col.name}
                            {col.type ? ` (${col.type})` : ''}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        placeholder="updatedAt, createdAt, id..."
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                        value={field}
                        onChange={e => update({ field: e.target.value })}
                        autoComplete="off"
                      />
                    )}

                    {/* Indicador del tipo detectado */}
                    {field && selectedColMeta && (
                      <p className="text-[10px] text-slate-400 mt-1 flex items-center gap-1">
                        <Info className="w-3 h-3" />
                        {inputType === 'date'
                          ? (t('filter.detected_date') || 'Detectado como campo de fecha — se usa selector de fecha')
                          : inputType === 'number'
                            ? (t('filter.detected_number') || 'Detectado como campo numérico — ingresa un valor directamente')
                            : (t('filter.detected_text') || 'Tipo de campo no reconocido — ingresa el valor como texto')
                        }
                      </p>
                    )}
                  </div>

                  {/* Valores Desde/Hasta: solo en modo manual */}
                  {mode === 'manual' && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {renderValueInput(
                        t('filter.from_label') || 'Desde',
                        fromValue,
                        (v) => update({ fromValue: v }),
                        true,
                        null,
                      )}
                      {renderValueInput(
                        t('filter.to_label') || 'Hasta',
                        toValue,
                        (v) => update({ toValue: v }),
                        false,
                        t('filter.to_hint') || 'Vacío = hasta el último registro disponible',
                      )}
                    </div>
                  )}

                  {/* Modo auto: solo muestra estado, sin inputs adicionales */}
                  {mode === 'auto' && !suggestedLabel && (
                    <div className="bg-slate-50 rounded-lg px-3 py-2 text-xs text-slate-500">
                      {t('filter.auto_no_history') || 'Sin ejecuciones previas. La primera vez se migran todos los datos. Desde la segunda, solo los cambios.'}
                    </div>
                  )}
                </div>
              )}

              {/* Warning OVERWRITE + filtro */}
              {enabled && (
                <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5 text-xs text-amber-700">
                  <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                  <span>{t('filter.overwrite_warning') || 'Si el destino usa OVERWRITE, el filtro limita qué datos se leen del origen, pero el destino se borrará por completo antes de escribir.'}</span>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default FilterConfigPanel;
